import { setCustomUrl, trackPageView } from '@snowplow/browser-tracker';
import { debounce, isNumber } from 'lodash';
import ReactGA from 'react-ga';
import type { Optional, Primitive } from 'utility-types';

import { GADimension } from '@swf/core/enums';
import type { TrackingContextValue } from '@swf/core/providers/TrackingContext';
import type { ButtonClick } from '@swf/generated/analytics/buttonClick';
import { buttonClick } from '@swf/generated/analytics/buttonClick';
import type { Entity } from '@swf/generated/analytics/entities';
import type { ErrorView } from '@swf/generated/analytics/errorView';
import { errorView } from '@swf/generated/analytics/errorView';
import type { InputFill } from '@swf/generated/analytics/inputFill';
import { inputFill } from '@swf/generated/analytics/inputFill';
import type { InputFocus } from '@swf/generated/analytics/inputFocus';
import { inputFocus } from '@swf/generated/analytics/inputFocus';
import type { LinkClick } from '@swf/generated/analytics/linkClick';
import { linkClick } from '@swf/generated/analytics/linkClick';
import type { ResultsView } from '@swf/generated/analytics/resultsView';
import { resultsView } from '@swf/generated/analytics/resultsView';
import type { TabClick } from '@swf/generated/analytics/tabClick';
import { tabClick } from '@swf/generated/analytics/tabClick';

import { GAEvent, getEntities } from './utils';

export const buttonPressed = (
  context: TrackingContextValue,
  snowplowEnabled: boolean,
  { value, ...data }: Optional<ButtonClick, 'buttonName'> & { value?: number },
  trackingDimensions?: Record<string, string | number>
) => {
  if (!data.buttonName) return;
  GAEvent({ action: 'Button Pressed', label: data.buttonName, value }, context, trackingDimensions);
  snowplowEnabled && buttonClick(data as ButtonClick, getEntities(context));
};

export const inputFocused = (
  context: TrackingContextValue,
  snowplowEnabled: boolean,
  data: Optional<InputFocus, 'fieldName'>
) => {
  if (!data.fieldName) return;
  GAEvent({ action: 'Input Focused', label: data.fieldName }, context);
  snowplowEnabled && inputFocus(data as InputFocus, getEntities(context));
};

export const inputFilled = (
  context: TrackingContextValue,
  snowplowEnabled: boolean,
  data: Optional<InputFill, 'fieldName' | 'isAutoFilled'>
) => {
  if (!data.fieldName) return;
  const value = isNumber(data.numInput) ? data.numInput : undefined;
  GAEvent({ action: 'Input Filled', label: data.fieldName, value }, context, {
    [GADimension.InputValue]: data.strInput || data.boolInput,
    [GADimension.ErrorsTracked]: data.fieldSuggestions?.join(',')
  });
  if (!data?.fieldErrors?.length) delete data.fieldErrors;
  snowplowEnabled && inputFill({ isAutoFilled: false, ...data } as InputFill, getEntities(context));
};

export const tabClicked = (
  context: TrackingContextValue,
  snowplowEnabled: boolean,
  data: TabClick & { tabIndex?: number },
  dimensions?: Record<string, Primitive | string[]>
) => {
  GAEvent({ action: 'Tab Pressed', label: data.tabName }, context, dimensions);
  snowplowEnabled && tabClick(data, getEntities(context));
};

export const linkClicked = (
  context: TrackingContextValue,
  snowplowEnabled: boolean,
  data: LinkClick
) => snowplowEnabled && linkClick(data, getEntities(context));

export const pageView = (
  context: TrackingContextValue,
  snowplowEnabled: boolean,
  pathname: string
) => {
  ReactGA.pageview(pathname);
  ReactGA.set({ [GADimension.PageOriginPath]: pathname });
  if (snowplowEnabled) {
    setCustomUrl(pathname);
    trackPageView();
  }
};

export const resultsViewed = debounce(
  (
    context: TrackingContextValue,
    snowplowEnabled: boolean,
    list: ResultsView,
    contents?: Entity[]
  ) => {
    const entities: Entity[] = getEntities(context) || [];
    contents && entities.push(...contents);
    snowplowEnabled && resultsView(list, entities.slice(0, 20));
  },
  300
);

export const errorViewed = (
  context: TrackingContextValue,
  snowplowEnabled: boolean,
  { errorCode }: Partial<ErrorView>
) => snowplowEnabled && errorCode && errorView({ errorCode }, getEntities(context));
